import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaPhone, FaEnvelope } from 'react-icons/fa';

const Contact: React.FC = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile: '',
        whatsapp: '',
        subject: '',
        message: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Use FormSubmit.co like Volunteer.tsx - Send email directly
            const response = await fetch('https://formsubmit.co/ajax/hello@mylvtrust.com', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    mobile: formData.mobile,
                    whatsapp: formData.whatsapp,
                    subject: formData.subject || 'Contact Form Submission',
                    message: formData.message
                })
            });

            const result = await response.json();

            if (result.success) {
                // Show success message and reset form
                setShowSuccess(true);
                setFormData({ name: '', email: '', mobile: '', whatsapp: '', subject: '', message: '' });
                console.log('Contact form submitted successfully');
            } else {
                throw new Error('Failed to submit form');
            }

        } catch (error) {
            console.error('Error submitting form:', error);
            // Fallback to mailto if FormSubmit fails
            const subject = encodeURIComponent(formData.subject || 'Contact Form Submission');
            const body = encodeURIComponent(
                `Name: ${formData.name}\n` +
                `Email: ${formData.email}\n` +
                `Mobile: ${formData.mobile}\n` +
                `WhatsApp: ${formData.whatsapp}\n\n` +
                `Message:\n${formData.message}`
            );

            const mailtoLink = `mailto:hello@mylvtrust.com?subject=${subject}&body=${body}`;
            window.open(mailtoLink, '_blank');

            // Still show success message
            setShowSuccess(true);
            setFormData({ name: '', email: '', mobile: '', whatsapp: '', subject: '', message: '' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section with Background Image */}
            <div className="relative w-full">
                <div
                    className="w-full min-h-[400px] bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: 'url(/backend%20log/contactbg.jpg)',
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                    }}
                >
                    {/* Dark overlay */}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="text-center text-white px-4">
                            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
                            <p className="text-xl md:text-2xl max-w-2xl mx-auto">
                                We'd love to hear from you. Get in touch with us for any questions or inquiries.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-16 -mt-10">
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="grid md:grid-cols-2">
                        {/* Contact Info */}
                        <div className="p-10 bg-gray-50">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">Get in Touch</h2>

                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <div className="bg-primary/10 p-3 rounded-lg text-primary mr-4">
                                        <FaMapMarkerAlt className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800">Location</h3>
                                        <p className="text-gray-600 mt-1">58, 59 F-3 DJ Garden Extension, Ramamurthy Road, Selvapuram, Coimbatore-641026, TN <svg className="w-5 h-3 ml-1 inline" viewBox="0 0 24 16" fill="none">
                                            <rect width="24" height="5.33" fill="#FF9933" />
                                            <rect width="24" height="5.33" y="5.33" fill="white" />
                                            <rect width="24" height="5.33" y="10.66" fill="#138808" />
                                            <circle cx="12" cy="8" r="1.8" fill="#000080" />
                                            <circle cx="12" cy="8" r="2.2" fill="none" stroke="#000080" strokeWidth="0.3" />
                                            <path d="M12 6.5 L12.4 7.3 L13.2 7.3 L12.6 7.8 L12.8 8.6 L12 8.1 L11.2 8.6 L11.4 7.8 L10.8 7.3 L11.6 7.3 Z" fill="#000080" />
                                        </svg></p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="bg-primary/10 p-3 rounded-lg text-primary mr-4">
                                        <FaPhone className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800">Phone</h3>
                                        <p className="text-gray-600 mt-1">+91 9942019000</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="bg-primary/10 p-3 rounded-lg text-primary mr-4">
                                        <FaEnvelope className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800">Email</h3>
                                        <p className="text-gray-600 mt-1">hello@mylvtrust.com</p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <img
                                    src="/images/ContctUsDetails.png"
                                    alt="Contact Us Details"
                                    className="w-full h-auto object-contain mt-2"
                                />
                            </div>

                            {/* <div className="mt-10">
                                <h3 className="font-semibold text-gray-800 mb-4">Follow Us</h3>
                                <div className="flex space-x-4">
                                    <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="bg-white p-3 rounded-full shadow-md text-gray-700 hover:bg-primary hover:text-white transition-colors">
                                        <FaLinkedin className="w-5 h-5" />
                                    </a>
                                    <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="bg-white p-3 rounded-full shadow-md text-gray-700 hover:bg-primary hover:text-white transition-colors">
                                        <FaFacebook className="w-5 h-5" />
                                    </a>
                                    <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="bg-white p-3 rounded-full shadow-md text-gray-700 hover:bg-primary hover:text-white transition-colors">
                                        <FaInstagram className="w-5 h-5" />
                                    </a>
                                    <a href="#" className="bg-white p-3 rounded-full shadow-md text-gray-700 hover:bg-primary hover:text-white transition-colors">
                                        <img src="/partner logo/CYBERXPERTZ.png" alt="CYBERXPERTZ" className="w-5 h-5 object-contain" />
                                    </a>
                                </div>
                            </div> */}
                        </div>

                        {/* Contact Form */}
                        <div className="p-10">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">Send Us a Message</h2>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                        placeholder="Enter your name"
                                        required
                                    />
                                </div>

                                <div>
                                    <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                                    <input
                                        type="tel"
                                        id="mobile"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                        placeholder="Enter your mobile number"
                                        pattern="[0-9]{10}"
                                        maxLength={10}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number</label>
                                    <input
                                        type="tel"
                                        id="whatsapp"
                                        name="whatsapp"
                                        value={formData.whatsapp}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                        placeholder="Enter your WhatsApp number"
                                        pattern="[0-9]{10}"
                                        maxLength={10}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Your Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                        placeholder="Enter your email"
                                        required
                                    />
                                </div>

                                <div>
                                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                    <input
                                        type="text"
                                        id="subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                        placeholder="Enter subject"
                                        required
                                    />
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        rows={4}
                                        value={formData.message}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none"
                                        placeholder="Enter your message"
                                        required
                                    />
                                </div>

                                <p className="text-xs text-gray-500 leading-relaxed text-center sm:text-left">
                                    We protect your personal data under the DPDP Act, 2023. By submitting this message, you acknowledge that your contact details will be used solely to respond to your inquiry in accordance with our{' '}
                                    <Link to="/privacy-policy" className="text-primary font-semibold underline hover:text-primary-dark" target="_blank" rel="noopener noreferrer">
                                        Privacy Policy &amp; Notice
                                    </Link>.
                                </p>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-primary text-white font-semibold py-3 px-6 rounded-lg hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50 disabled:opacity-70"
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Map Section
                <div className="mt-16 rounded-xl overflow-hidden shadow-lg">
                    <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15664.21598364238!2d76.9559!3d11.0168!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba8582f6295d4f9%3A0x1f3a0774f0b1a8a0!2sCoimbatore%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin"
                        width="100%"
                        height="450"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Our Location"
                    />
                </div> */}
            </div>

            {/* Success Popup */}
            {showSuccess && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                                <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                            <p className="text-gray-600 mb-6">Your message has been sent successfully. We'll get back to you soon!</p>
                            <button
                                onClick={() => setShowSuccess(false)}
                                className="w-full bg-primary text-white font-semibold py-3 px-6 rounded-lg hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Contact;